import {
  IDynamicFormFieldsJson,
  IField,
  IOption,
} from "../../models/user/DynamicForm/DynamicForm";
import { TranscriptTurn } from "./voiceLiveDemoLogic";

export interface DynamicFieldStatus {
  label: string;
  value: string;
  status: "missing" | "complete" | "pending";
  source: string;
}

const MONTH_NAMES =
  "january|february|march|april|may|june|july|august|september|october|november|december";

function titleCaseMonth(month: string): string {
  return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
}

function parseDayToken(raw: string): string | null {
  const token = raw.trim().replace(/[,.]$/, "");
  const numeric = token.match(/^(\d{1,2})(?:st|nd|rd|th)?$/i);
  if (numeric) return numeric[1];
  return null;
}

function extractDateFromSpeech(text: string): string | null {
  const trimmed = text.trim();
  const numeric = trimmed.match(
    /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})\b/
  );
  if (numeric) {
    const y = numeric[3].length === 2 ? `20${numeric[3]}` : numeric[3];
    return `${numeric[1]}/${numeric[2]}/${y}`;
  }

  const monthFirst = trimmed.match(
    new RegExp(
      `\\b(?:on\\s+)?(${MONTH_NAMES})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?\\b`,
      "i"
    )
  );
  if (monthFirst) {
    const month = titleCaseMonth(monthFirst[1]);
    const day = parseDayToken(monthFirst[2]) || monthFirst[2];
    const year = monthFirst[3] || String(new Date().getFullYear());
    return `${month} ${day}, ${year}`;
  }

  const dayFirst = trimmed.match(
    new RegExp(
      `\\b(?:on\\s+)?(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH_NAMES})(?:,?\\s+(\\d{4}))?\\b`,
      "i"
    )
  );
  if (dayFirst) {
    const day = parseDayToken(dayFirst[1]) || dayFirst[1];
    const month = titleCaseMonth(dayFirst[2]);
    const year = dayFirst[3] || String(new Date().getFullYear());
    return `${month} ${day}, ${year}`;
  }

  if (/yesterday/i.test(trimmed)) {
    const slash = trimmed.match(/\b(\d{1,2})[\/\-](\d{1,2})(?:[\/\-](\d{2,4}))?\b/);
    if (slash) {
      const monthNum = parseInt(slash[1], 10);
      const dayNum = parseInt(slash[2], 10);
      const year = slash[3]
        ? slash[3].length === 2
          ? `20${slash[3]}`
          : slash[3]
        : String(new Date().getFullYear());
      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      if (monthNum >= 1 && monthNum <= 12) {
        return `${monthNames[monthNum - 1]} ${dayNum}, ${year}`;
      }
    }
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${monthNames[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }

  const slashDate = trimmed.match(/\b(\d{1,2})[\/\-](\d{1,2})(?:[\/\-](\d{2,4}))?\b/);
  if (slashDate) {
    const monthNum = parseInt(slashDate[1], 10);
    const dayNum = parseInt(slashDate[2], 10);
    const year = slashDate[3]
      ? slashDate[3].length === 2
        ? `20${slashDate[3]}`
        : slashDate[3]
      : String(new Date().getFullYear());
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    if (monthNum >= 1 && monthNum <= 12) {
      return `${monthNames[monthNum - 1]} ${dayNum}, ${year}`;
    }
  }

  return null;
}

function extractTimeFromSpeech(text: string): string | null {
  const match =
    text.match(/\b(\d{1,2}:\d{2}\s*(?:a\.?m\.?|p\.?m\.?))\b/i) ||
    text.match(/\b(\d{1,2})\s*(a\.?m\.?|p\.?m\.?)\b/i);
  if (!match) return null;
  return match[1].trim();
}

function normalizeLabel(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function isChoiceField(field: IField): boolean {
  return (
    field.type === "options" ||
    field.type === "select" ||
    field.type === "radio" ||
    field.type === "checkbox" ||
    field.type === "toggle"
  );
}

function isPolicySchemaField(field: IField): boolean {
  return /policy/i.test(field.name) || /policy\s*#/i.test(field.label);
}

function isMileageField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return label.includes("mileage") || /mileage/i.test(field.name);
}

function isUsageField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return (
    label.includes("vehicle usage") ||
    (/usage/i.test(field.name) && !/mileage/i.test(field.name))
  );
}

function isVehicleTypeField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return label.includes("vehicle type") || /vehicleType/i.test(field.name);
}

function isOwnershipField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return label.includes("vehicle ownership") || /ownership/i.test(field.name);
}

function isGarageField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return (
    label.includes("garage") ||
    label.includes("garaging") ||
    /garage|garaging/i.test(field.name)
  );
}

function isParkedAtPrimaryField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return label.includes("parked") && label.includes("primary");
}

function looksLikeModelYear(value: string): boolean {
  return /^(19|20)\d{2}$/.test(value.trim());
}

function isPlausibleMileage(value: string): boolean {
  const n = parseInt(value.replace(/,/g, ""), 10);
  if (!Number.isFinite(n)) return false;
  if (looksLikeModelYear(value)) return false;
  return n >= 100 && n <= 999999;
}

function isInvalidLiteralFieldValue(field: IField, value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return true;
  if (isMakeField(field) && /^(make|model|year|vehicle)$/.test(v)) return true;
  if (isModelField(field) && /^(make|model|year|vehicle)$/.test(v)) return true;
  if (isVehicleTypeField(field) && v.length > 48) return true;
  if (isMileageField(field) && !isPlausibleMileage(value)) return true;
  return false;
}

function isPrimaryUseStateField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return (
    label.includes("primary use state") ||
    label.includes("use state") ||
    label.includes("loc state") ||
    /primaryUseState|useState|locState/i.test(field.name)
  );
}

function matchKeywordToOption(
  field: IField,
  keywords: Array<{ pattern: RegExp; needle: string }>
): string | null {
  if (!field.options?.length) return null;
  for (const { pattern, needle } of keywords) {
    if (!pattern.test(needle)) continue;
    for (const opt of field.options) {
      const label = opt.label.toLowerCase();
      const code = opt.code.toLowerCase();
      if (label.includes(needle) || code.includes(needle)) return opt.code;
    }
  }
  return null;
}

function matchOptionValue(field: IField, spoken: string): string | null {
  const lower = spoken.toLowerCase().trim();
  if (!lower) return null;

  if (field.options?.length) {
    for (const opt of field.options) {
      const label = opt.label.toLowerCase();
      const code = opt.code.toLowerCase();
      if (lower === label || lower === code) return opt.code;
    }

    if (lower.length <= 40) {
      for (const opt of field.options) {
        const label = opt.label.toLowerCase();
        const code = opt.code.toLowerCase();
        if (label.length >= 4 && lower.includes(label)) return opt.code;
        if (code.length >= 3 && lower.includes(code)) return opt.code;
      }
    }
  }

  if (isVehicleTypeField(field)) {
    return matchKeywordToOption(field, [
      { pattern: /\bsuv\b/i, needle: "suv" },
      { pattern: /\bsedan\b/i, needle: "sedan" },
      { pattern: /\btruck\b/i, needle: "truck" },
      { pattern: /\bvan\b/i, needle: "van" },
      { pattern: /\bcoupe\b/i, needle: "coupe" },
      { pattern: /\bwagon\b/i, needle: "wagon" },
    ]);
  }

  if (isUsageField(field)) {
    return matchKeywordToOption(field, [
      { pattern: /\bbusiness\b/i, needle: "business" },
      { pattern: /\bcommute\b/i, needle: "commute" },
      { pattern: /\bpleasure\b/i, needle: "pleasure" },
      { pattern: /\bpersonal\b/i, needle: "personal" },
      { pattern: /\bfarm\b/i, needle: "farm" },
    ]);
  }

  if (isOwnershipField(field)) {
    const owned = matchKeywordToOption(field, [
      { pattern: /\bleased?\b/i, needle: "lease" },
      { pattern: /\bowned?\b/i, needle: "own" },
      { pattern: /\bfinanc/i, needle: "financ" },
    ]);
    if (owned) return owned;
    if (/\bleased?\b/i.test(lower)) return "leased";
    if (/\bowned?\b/i.test(lower)) return "owned";
    if (/\bfinanc/i.test(lower)) return "financed";
  }

  if (isUsageField(field) && !field.options?.length) {
    if (/\bbusiness\b/i.test(lower)) return "business";
    if (/\bcommute\b/i.test(lower)) return "commute";
    if (/\bpleasure\b/i.test(lower)) return "pleasure";
    if (/\bpersonal\b/i.test(lower)) return "personal";
  }

  if (isVehicleTypeField(field) && !field.options?.length) {
    if (/\bsuv\b/i.test(lower)) return "suv";
    if (/\bsedan\b/i.test(lower)) return "sedan";
    if (/\btruck\b/i.test(lower)) return "truck";
  }

  return null;
}

/** Pull vehicle type + usage from one short answer (e.g. "SUV for business use"). */
function parseVehicleTypeAndUsageFromSpeech(text: string): {
  type?: string;
  usage?: string;
} {
  const lower = text.toLowerCase();
  const out: { type?: string; usage?: string } = {};
  if (/\bsuv\b/i.test(lower)) out.type = "suv";
  else if (/\bsedan\b/i.test(lower)) out.type = "sedan";
  else if (/\btruck\b/i.test(lower)) out.type = "truck";
  else if (/\bvan\b/i.test(lower)) out.type = "van";
  if (/\bbusiness\b/i.test(lower)) out.usage = "business";
  else if (/\bcommute\b/i.test(lower)) out.usage = "commute";
  else if (/\bpleasure\b/i.test(lower)) out.usage = "pleasure";
  else if (/\bpersonal\b/i.test(lower)) out.usage = "personal";
  return out;
}

function formatDisplayValue(field: IField, raw: string): string {
  const text = String(raw).trim();
  if (!text || !field.options?.length) return text;
  const hit = field.options.find(
    (o) =>
      o.code === text ||
      o.label.toLowerCase() === text.toLowerCase() ||
      o.code.toLowerCase() === text.toLowerCase()
  );
  return hit?.label || text;
}

const ANSWER_FILLER =
  /^(yes|no|yeah|yep|nope|ok|okay|sure|thanks|thank you|got it|correct|right|hello|hi)$/i;

function isUsableAnswer(text: string): boolean {
  const t = text.trim();
  if (!t || t.length > 120) return false;
  if (ANSWER_FILLER.test(t)) return false;
  return true;
}

function isYearField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  const name = field.name.toLowerCase();
  return (
    field.type === "number" ||
    /year/i.test(name) ||
    label.includes("manufacture year") ||
    label.includes("model year")
  );
}

function isMakeField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return label === "make" || /vehicle make/i.test(field.label);
}

function isModelField(field: IField): boolean {
  const label = normalizeLabel(field.label);
  return label === "model" || /vehicle model/i.test(field.label);
}

function isEffectiveDateField(field: IField): boolean {
  return (
    field.type === "date" ||
    normalizeLabel(field.label).includes("effective date")
  );
}

/** Parse "2015 Honda Accord", "year is 2015", "model is Civic". */
function parseVehicleSpeech(text: string): {
  year?: string;
  make?: string;
  model?: string;
  effectiveDate?: string;
} {
  const out: Record<string, string> = {};
  const trimmed = text.trim();
  if (!trimmed) return out;

  const effective = trimmed.match(
    /\beffective\s+date\s+(?:is|from)\s+([^.;]+?)(?:\s+and\b|[.;]|$)/i
  );
  if (effective) {
    out.effectiveDate =
      extractDateFromSpeech(effective[1].trim()) || effective[1].trim();
  }

  const yearLabeled = trimmed.match(/\byear\s+is\s+((?:19|20)\d{2})\b/i);
  if (yearLabeled) out.year = yearLabeled[1];

  const modelLabeled = trimmed.match(/\bmodel\s+is\s+([A-Za-z][A-Za-z0-9-]*)\b/i);
  if (modelLabeled) out.model = modelLabeled[1];

  const makeLabeled = trimmed.match(/\bmake\s+is\s+([A-Za-z][A-Za-z0-9-]*)\b/i);
  if (makeLabeled) out.make = makeLabeled[1];

  const labeledYmm = trimmed.match(
    /\byear\s+((?:19|20)\d{2})\s+make\s+([A-Za-z][A-Za-z0-9-]*)\s+model\s+(.+?)(?=\s+and\s+|\s+effective\b|[.;]|$)/i
  );
  if (labeledYmm) {
    out.year = labeledYmm[1];
    out.make = labeledYmm[2];
    out.model = labeledYmm[3].trim();
  }

  const ymm = trimmed.match(
    /\b((?:19|20)\d{2})\s+([A-Za-z]+)(?:\s+([A-Za-z][A-Za-z0-9-]*))?\b/
  );
  if (ymm) {
    if (!out.year) out.year = ymm[1];
    if (!out.make) out.make = ymm[2];
    if (!out.model && ymm[3]) out.model = ymm[3];
  }

  if (!out.effectiveDate) {
    const dateOnly = extractDateFromSpeech(trimmed);
    if (dateOnly && !/\b(19|20)\d{2}\s+[A-Za-z]+\b/.test(trimmed)) {
      out.effectiveDate = dateOnly;
    }
  }

  return out;
}

function applyVehicleSpeechToFields(
  schema: IDynamicFormFieldsJson,
  speech: ReturnType<typeof parseVehicleSpeech>,
  merged: Record<string, string>
): void {
  for (const field of flattenDynamicFields(schema)) {
    if (isYearField(field) && speech.year && !merged[field.name]) {
      merged[field.name] = speech.year;
    }
    if (isMakeField(field) && speech.make && !merged[field.name]) {
      merged[field.name] = speech.make;
    }
    if (isModelField(field) && speech.model && !merged[field.name]) {
      merged[field.name] = speech.model;
    }
    if (isEffectiveDateField(field) && speech.effectiveDate && !merged[field.name]) {
      merged[field.name] = speech.effectiveDate;
    }
  }
}

/** True when the agent turn is asking for this form field. */
function agentAskedForField(field: IField, agentText: string): boolean {
  const label = normalizeLabel(field.label);
  const agent = normalizeLabel(agentText);
  if (!label || label.length < 3) return false;
  if (agent.includes(label)) return true;

  const synonyms: Record<string, string[]> = {
    "effective date": ["effective date", "coverage start", "start date"],
    "first name": ["first name", "given name"],
    "middle name": ["middle name"],
    "last name": ["last name", "surname", "family name"],
    "marital status": ["marital status", "married or single"],
    gender: ["gender", "male or female", "sex"],
    "date of birth": ["date of birth", "birthday", "born", "dob"],
    "relationship to insured": ["relationship to insured", "relationship"],
    "employment status": ["employment status", "employed"],
    "educational qualification": ["education", "educational"],
    "date first licensed": ["first licensed", "license date"],
    "license number": ["license number", "driver license number", "dl number"],
    "licensing state": ["licensing state", "license state", "which state"],
    "license status": ["license status"],
    prefix: ["prefix", "title", "mr or mrs"],
    "manufacture year": [
      "model year",
      "vehicle year",
      "year of the vehicle",
      "year, make",
      "what year",
    ],
    make: ["vehicle make", "what make", "manufacturer", "year, make", "make and model"],
    model: ["vehicle model", "what model", "make and model", "which model"],
    "vehicle type": [
      "vehicle type",
      "type of vehicle",
      "sedan or suv",
      "suv or sedan",
    ],
    "current mileage": [
      "current mileage",
      "mileage",
      "odometer",
      "how many miles",
      "miles on the vehicle",
    ],
    "annual mileage": ["annual mileage", "miles per year"],
    "vehicle usage": [
      "vehicle usage",
      "how do you use",
      "how is the vehicle used",
      "primary use of the vehicle",
      "personal or business",
      "business use",
      "business or personal",
    ],
    "vehicle primary use state": [
      "primary use state",
      "which state",
      "vehicle primarily used",
      "used in what state",
      "garaging state",
    ],
    "homeowners loc state": ["loc state", "location state", "garaging state"],
    "vehicle ownership": [
      "ownership",
      "owned or leased",
      "do you own",
      "own or lease",
      "lease the vehicle",
    ],
    "garage location": [
      "garage location",
      "garaging address",
      "garaging location",
      "where is the vehicle garaged",
      "primary garaging",
    ],
    vin: ["vin", "vehicle identification"],
  };

  for (const [key, phrases] of Object.entries(synonyms)) {
    if (!label.includes(key.replace(/_/g, " ")) && label !== key) continue;
    if (phrases.some((p) => agent.includes(normalizeLabel(p)))) return true;
  }

  const nameWords = field.name.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  if (nameWords.length > 4 && agent.includes(nameWords)) return true;

  return false;
}

function parseShortAnswerForField(
  field: IField,
  answer: string,
  agentText: string
): string | null {
  const trimmed = answer.trim();
  if (!isUsableAnswer(trimmed)) return null;

  const fromPatterns = extractValueForField(field, trimmed);
  if (fromPatterns) return fromPatterns;

  if (field.type === "date") {
    return extractDateFromSpeech(trimmed);
  }

  const labelNorm = normalizeLabel(field.label);

  if (labelNorm.includes("first name")) {
    const m = trimmed.match(/^([A-Za-z][A-Za-z'.-]*)/);
    if (m) return m[1];
  }
  if (labelNorm.includes("last name")) {
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length) return parts[parts.length - 1].replace(/[.,]$/, "");
  }
  if (labelNorm.includes("middle name")) {
    const m = trimmed.match(/^([A-Za-z][A-Za-z'.-]*)/);
    if (m) return m[1];
  }
  if (labelNorm === "prefix" || labelNorm.includes("prefix")) {
    const m = trimmed.match(/^(mr|mrs|ms|miss|dr)\.?$/i);
    if (m) return m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase() + ".";
  }
  if (labelNorm.includes("license number")) {
    const m = trimmed.match(/\b([A-Z0-9][A-Z0-9-]{4,20})\b/i);
    if (m) return m[1].toUpperCase();
  }
  if (isYearField(field)) {
    const m = trimmed.match(/\b(19|20)\d{2}\b/);
    if (m) return m[0];
  }

  if (isMakeField(field)) {
    const vehicle = parseVehicleSpeech(trimmed);
    if (vehicle.make) return vehicle.make;
    const stripped = trimmed.replace(/^\s*(?:19|20)\d{2}\s+/, "").trim();
    if (stripped && /^[A-Za-z][A-Za-z0-9-]*$/.test(stripped)) return stripped;
  }

  if (isModelField(field)) {
    const vehicle = parseVehicleSpeech(trimmed);
    if (vehicle.model) return vehicle.model;
    if (/^[A-Za-z][A-Za-z0-9-]*$/.test(trimmed)) return trimmed;
  }

  if (isEffectiveDateField(field)) {
    const vehicle = parseVehicleSpeech(trimmed);
    if (vehicle.effectiveDate) return vehicle.effectiveDate;
    const dateOnly = extractDateFromSpeech(trimmed);
    if (dateOnly) return dateOnly;
  }

  if (isMileageField(field)) {
    const miles = trimmed.match(/\b([\d,]{2,7})\s*(?:miles|mi)\b/i);
    if (miles && isPlausibleMileage(miles[1])) return miles[1].replace(/,/g, "");
    const num = trimmed.match(/^\s*([\d,]{2,7})\s*$/);
    if (num && isPlausibleMileage(num[1])) return num[1].replace(/,/g, "");
  }

  if (isPrimaryUseStateField(field)) {
    const state = trimmed.match(/\b([A-Z]{2})\b/);
    if (state) return state[1];
  }

  if (field.type === "checkbox" || field.type === "toggle") {
    if (/^(yes|yeah|yep|true)$/i.test(trimmed)) return "true";
    if (/^(no|nope|false)$/i.test(trimmed)) return "false";
  }

  if (isChoiceField(field)) {
    return matchOptionValue(field, trimmed);
  }

  if (
    field.type === "text" &&
    !labelNorm.includes("description") &&
    !isMakeField(field) &&
    !isModelField(field) &&
    trimmed.length <= 64 &&
    /^[A-Za-z0-9][A-Za-z0-9\s.,'/-]*$/.test(trimmed)
  ) {
    if (/^(what|when|where|how|why|can|could|would|is|are)\b/i.test(trimmed)) {
      return null;
    }
    return trimmed.replace(/[.,]$/, "");
  }

  return null;
}

/** Map agent question → next claimant reply to form fields. */
function extractFromAgentClaimantPairs(
  schema: IDynamicFormFieldsJson,
  turns: TranscriptTurn[]
): Record<string, string> {
  const extracted: Record<string, string> = {};
  const fields = flattenDynamicFields(schema);
  const ordered = turns.filter((t) => t.text?.trim() && !t.streaming);

  for (let i = 0; i < ordered.length; i += 1) {
    const turn = ordered[i];
    if (turn.speaker !== "Agent") continue;
    const agentText = turn.text!.trim();

    let claimantText = "";
    for (let j = i + 1; j < ordered.length; j += 1) {
      const next = ordered[j];
      if (next.speaker === "Claimant") {
        claimantText = next.text!.trim();
        break;
      }
      if (next.speaker === "Agent") break;
    }
    if (!claimantText) continue;

    const vehicleFromAnswer = parseVehicleSpeech(claimantText);
    const agentAsksYmm = /\b(year|make|and model|make and model)\b/i.test(
      agentText
    );

    for (const field of fields) {
      if (extracted[field.name]) continue;
      if (!agentAskedForField(field, agentText)) continue;

      if (agentAsksYmm) {
        if (isYearField(field) && vehicleFromAnswer.year) {
          extracted[field.name] = vehicleFromAnswer.year;
          continue;
        }
        if (isMakeField(field) && vehicleFromAnswer.make) {
          extracted[field.name] = vehicleFromAnswer.make;
          continue;
        }
        if (isModelField(field) && vehicleFromAnswer.model) {
          extracted[field.name] = vehicleFromAnswer.model;
          continue;
        }
      }

      const value = parseShortAnswerForField(field, claimantText, agentText);
      if (value && !isInvalidLiteralFieldValue(field, value)) {
        extracted[field.name] = value;
      }
    }

    const combinedTypeUsage =
      /\b(sedan|suv|truck|type of vehicle)\b/i.test(agentText) &&
      /\b(business|personal|pleasure|commute|usage|use)\b/i.test(agentText);
    if (combinedTypeUsage) {
      const parsed = parseVehicleTypeAndUsageFromSpeech(claimantText);
      for (const field of fields) {
        if (extracted[field.name]) continue;
        if (isVehicleTypeField(field) && parsed.type) {
          const code = matchOptionValue(field, parsed.type);
          if (code) extracted[field.name] = code;
        }
        if (isUsageField(field) && parsed.usage) {
          const code = matchOptionValue(field, parsed.usage);
          if (code) extracted[field.name] = code;
        }
      }
    }
  }

  return extracted;
}

function extractValueForField(field: IField, text: string): string | null {
  const lower = text.toLowerCase();
  const labelNorm = normalizeLabel(field.label);
  const nameNorm = field.name.replace(/([A-Z])/g, " $1").toLowerCase().trim();

  const labelPatterns = [
    new RegExp(
      `\\b(?:my\\s+)?${escapeRegex(labelNorm)}\\s+(?:is|was|are)\\s+([^.,;]+)`,
      "i"
    ),
    new RegExp(`\\b${escapeRegex(nameNorm)}\\s+(?:is|was)\\s+([^.,;]+)`, "i"),
  ];
  for (const pattern of labelPatterns) {
    const m = text.match(pattern);
    if (m?.[1]?.trim()) return m[1].trim();
  }

  if (field.type === "date") {
    return extractDateFromSpeech(text);
  }
  if (field.type === "time") {
    return extractTimeFromSpeech(text);
  }
  if (field.name.toLowerCase() === "vin" || labelNorm.includes("vin")) {
    const vin = text.match(/\b([A-HJ-NPR-Z0-9]{17})\b/i);
    if (vin) return vin[1].toUpperCase();
  }
  if (isMileageField(field)) {
    const miles = text.match(/\b([\d,]{3,7})\s*(?:miles|mi)\b/i);
    if (miles && isPlausibleMileage(miles[1])) return miles[1].replace(/,/g, "");
    return null;
  }
  if (field.type === "number" || field.name.match(/year|age/i)) {
    const num = text.match(new RegExp(`\\b${escapeRegex(labelNorm)}\\s+(?:is\\s+)?(\\d+)`, "i"));
    if (num) return num[1];
  }
  if (isChoiceField(field)) {
    return null;
  }
  if (field.type === "checkbox" || field.type === "toggle") {
    if (/\b(yes|yeah|true|correct)\b/i.test(lower)) return "true";
    if (/\b(no|false)\b/i.test(lower)) return "false";
  }
  if (field.type === "textarea" && labelNorm) {
    const m = text.match(
      new RegExp(`\\b${escapeRegex(labelNorm)}\\s*[:,-]?\\s*([^.]{12,240})`, "i")
    );
    if (m?.[1]) return m[1].trim();
  }

  return null;
}

export function flattenDynamicFields(schema: IDynamicFormFieldsJson): IField[] {
  const fields: IField[] = [];
  for (const section of schema.sections || []) {
    for (const field of section.fields || []) {
      if (!field.hidden) fields.push(field);
    }
  }
  return fields;
}

export function extractDynamicFieldsFromText(
  schema: IDynamicFormFieldsJson,
  fullText: string
): Record<string, string> {
  const extracted: Record<string, string> = {};
  if (!fullText.trim()) return extracted;

  applyVehicleSpeechToFields(schema, parseVehicleSpeech(fullText), extracted);

  for (const field of flattenDynamicFields(schema)) {
    if (isChoiceField(field)) continue;
    if (
      isUsageField(field) ||
      isOwnershipField(field) ||
      isVehicleTypeField(field) ||
      isPrimaryUseStateField(field)
    ) {
      continue;
    }
    if (isMileageField(field)) continue;
    const value = extractValueForField(field, fullText);
    if (value && !isInvalidLiteralFieldValue(field, value)) {
      extracted[field.name] = value;
    }
  }

  for (const field of flattenDynamicFields(schema)) {
    if (isMileageField(field)) {
      const miles = fullText.match(/\b([\d,]{3,7})\s*(?:miles|mi)\b/i);
      if (miles && isPlausibleMileage(miles[1])) {
        extracted[field.name] = miles[1].replace(/,/g, "");
      }
    }
  }

  const email = fullText.match(/\b[\w.+-]+@[\w.-]+\.\w{2,}\b/);
  if (email) {
    for (const field of flattenDynamicFields(schema)) {
      if (/email/i.test(field.name) && !extracted[field.name]) {
        extracted[field.name] = email[0];
      }
    }
  }

  const phone = fullText.match(/\b(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})\b/);
  if (phone) {
    for (const field of flattenDynamicFields(schema)) {
      if (/phone|mobile|contact/i.test(field.name) && !extracted[field.name]) {
        extracted[field.name] = phone[1];
      }
    }
  }

  return extracted;
}

export function extractDynamicFieldsFromTranscript(
  schema: IDynamicFormFieldsJson,
  turns: TranscriptTurn[]
): Record<string, string> {
  const merged: Record<string, string> = {
    ...extractFromAgentClaimantPairs(schema, turns),
  };

  for (const turn of turns) {
    if (!turn.text?.trim() || turn.streaming) continue;
    if (turn.speaker !== "Claimant") continue;
    const partial = extractDynamicFieldsFromText(schema, turn.text);
    for (const [key, value] of Object.entries(partial)) {
      if (!value) continue;
      const field = flattenDynamicFields(schema).find((f) => f.name === key);
      if (field && isMakeField(field) && /^\s*(?:19|20)\d{2}\s+/.test(value)) {
        const vehicle = parseVehicleSpeech(value);
        if (vehicle.make) merged[key] = vehicle.make;
        if (vehicle.year) {
          const yearField = flattenDynamicFields(schema).find(isYearField);
          if (yearField && !merged[yearField.name]) {
            merged[yearField.name] = vehicle.year;
          }
        }
        continue;
      }
      if (!field) continue;
      if (!isInvalidLiteralFieldValue(field, value)) {
        merged[key] = value;
      }
    }
    applyVehicleSpeechToFields(schema, parseVehicleSpeech(turn.text), merged);
  }

  for (const field of flattenDynamicFields(schema)) {
    const raw = merged[field.name];
    if (raw && isInvalidLiteralFieldValue(field, raw)) {
      delete merged[field.name];
    }
  }

  const claimantFull = turns
    .filter((t) => t.speaker === "Claimant" && t.text?.trim() && !t.streaming)
    .map((t) => t.text)
    .join(" ");
  if (claimantFull.trim().length >= 8) {
    applyVehicleSpeechToFields(schema, parseVehicleSpeech(claimantFull), merged);
    const narrative = extractDynamicFieldsFromText(schema, claimantFull);
    for (const [key, value] of Object.entries(narrative)) {
      if (!value || merged[key]) continue;
      const field = flattenDynamicFields(schema).find((f) => f.name === key);
      if (!field || isChoiceField(field)) continue;
      if (!isInvalidLiteralFieldValue(field, value)) {
        merged[key] = value;
      }
    }
  }

  return merged;
}

export function applyExtractedToFormSchema(
  schema: IDynamicFormFieldsJson,
  extracted: Record<string, string>
): IDynamicFormFieldsJson {
  if (!Object.keys(extracted).length) return schema;
  return {
    ...schema,
    sections: schema.sections?.map((section) => ({
      ...section,
      fields: section.fields.map((field) => {
        const next = extracted[field.name];
        if (!next) return field;
        return { ...field, value: next };
      }),
    })),
  };
}

export function buildDynamicFieldStatuses(
  schema: IDynamicFormFieldsJson,
  extracted: Record<string, string>
): Record<string, DynamicFieldStatus> {
  const out: Record<string, DynamicFieldStatus> = {};
  for (const field of flattenDynamicFields(schema)) {
    const fromTranscript = extracted[field.name];
    const fromSchema =
      isPolicySchemaField(field) && field.value != null
        ? String(field.value).trim()
        : "";
    const raw = fromTranscript ?? (fromSchema || undefined);
    const hasValue =
      raw !== undefined &&
      raw !== null &&
      String(raw).trim() !== "" &&
      String(raw).trim() !== "false";
    const display = hasValue ? formatDisplayValue(field, String(raw)) : "";
    out[field.name] = {
      label: field.label,
      value: hasValue ? display : `Missing: ${field.label.toLowerCase()}`,
      status: hasValue ? "complete" : field.required ? "missing" : "pending",
      source: hasValue ? "live transcript" : "-",
    };
  }
  return out;
}

export function getNextMissingDynamicFieldLabel(
  schema: IDynamicFormFieldsJson,
  statuses: Record<string, DynamicFieldStatus>
): string | null {
  for (const field of flattenDynamicFields(schema)) {
    if (!field.required) continue;
    const st = statuses[field.name];
    if (!st || st.status === "missing") {
      return field.label;
    }
  }
  return null;
}

export function buildDynamicRequiredFieldLabels(
  schema: IDynamicFormFieldsJson
): string[] {
  return flattenDynamicFields(schema)
    .filter((f) => f.required)
    .map((f) => f.label);
}

export function getMissingRequiredDynamicFieldLabels(
  schema: IDynamicFormFieldsJson,
  statuses: Record<string, DynamicFieldStatus>
): string[] {
  return flattenDynamicFields(schema)
    .filter((f) => {
      if (!f.required) return false;
      const st = statuses[f.name];
      return !st || st.status === "missing";
    })
    .map((f) => f.label);
}

export function getNextDynamicFieldQuestion(
  schema: IDynamicFormFieldsJson,
  statuses: Record<string, DynamicFieldStatus>
): string | null {
  const label = getNextMissingDynamicFieldLabel(schema, statuses);
  if (!label) return null;
  const lower = label.toLowerCase();
  if (/^(what|when|where|which|please|can you)/i.test(label.trim())) {
    return label.trim().endsWith("?") ? label : `${label}?`;
  }
  if (lower.includes("current mileage") || lower.includes("mileage")) {
    return "What is the current mileage on the vehicle?";
  }
  if (lower.includes("vehicle usage")) {
    return "How is the vehicle used — for example commute, business, or pleasure?";
  }
  if (lower.includes("vehicle ownership")) {
    return "Do you own, lease, or finance this vehicle?";
  }
  if (lower.includes("primary use state") || lower.includes("use state")) {
    return "What state is the vehicle primarily used in?";
  }
  if (lower.includes("garage") || lower.includes("garaging")) {
    return "What is the primary garaging address for this vehicle, including city and zip code?";
  }
  if (lower.includes("parked") && lower.includes("primary")) {
    return "Is the vehicle parked at the primary garaging location?";
  }
  if (lower.includes("vehicle type")) {
    return "What type of vehicle is it — for example sedan, SUV, or truck?";
  }
  if (lower.includes("vin")) {
    return "What is the vehicle VIN?";
  }
  if (lower.includes("date of birth") || lower.includes("date first")) {
    return `What is the ${lower}?`;
  }
  if (lower.includes("effective date") || lower.includes("date")) {
    return `What is the ${lower}?`;
  }
  if (lower.includes("year")) {
    return "What is the vehicle year?";
  }
  if (lower === "make") {
    return "What is the vehicle make?";
  }
  if (lower === "model") {
    return "What is the vehicle model?";
  }
  return `What is the ${lower}?`;
}
